import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logWarn } from "@/lib/log";
import { Calendar, Sparkles } from "lucide-react";

/**
 * "Pick your Melo Monday drop" — Studio card.
 * Artist picks ONE track from their catalog to give away free every Monday.
 * Fans have to visit each artist profile to find the picks — creating a
 * weekly discovery ritual across the whole platform.
 */
export default function MeloMondayPanel({ tracks }) {
  const [current, setCurrent] = useState({ track_id: null, track: null, active_today: false });
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/artist/melo-monday")
      .then((r) => { setCurrent(r.data); setChoice(r.data.track_id || ""); })
      .catch((e) => logWarn("[melo-monday] load failed", e?.response?.status));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/artist/melo-monday", { track_id: choice || null });
      const refreshed = await api.get("/artist/melo-monday");
      setCurrent(refreshed.data);
      toast.success(choice ? "Melo Monday pick locked in" : "Melo Monday pick cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="melo-monday-panel" className="border border-[#2A2A2A] bg-[#121212] p-5">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="h-5 w-5 text-[#FF4500]" />
        <div className="font-display text-lg">Melo Mondays</div>
        {current.active_today && (
          <span className="ml-auto font-mono text-[9px] uppercase tracking-widest bg-[#FF4500] text-black px-2 py-1">
            LIVE TODAY
          </span>
        )}
      </div>
      <p className="font-mono text-[11px] text-gray-400 leading-relaxed mb-4">
        Pick one track to give away free every Monday. Fans hunt across every artist profile to find them.
        <span className="text-[#FF4500]"> Start digging!!</span>
      </p>

      <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500 block mb-2">
        Your Monday pick
      </label>
      <select
        data-testid="melo-monday-select"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        className="w-full bg-black border border-[#2A2A2A] focus:border-[#FF4500] outline-none px-3 py-2 font-mono text-sm text-white"
      >
        <option value="">— No pick this week —</option>
        {tracks.map((t) => (
          <option key={t.id} value={t.id}>{t.title}</option>
        ))}
      </select>

      {current.track && (
        <div className="mt-3 flex items-center gap-2 font-mono text-[11px] text-gray-400">
          <Sparkles className="h-3 w-3 text-[#FF4500]" />
          Currently locked: <span className="text-white">{current.track.title}</span>
        </div>
      )}

      <button
        data-testid="melo-monday-save"
        onClick={save}
        disabled={saving || choice === (current.track_id || "")}
        className="mt-4 w-full rounded-none bg-[#FF4500] hover:bg-[#E53E00] disabled:bg-[#2A2A2A] disabled:text-gray-500 text-black font-mono text-xs uppercase tracking-widest py-2.5 transition-colors"
      >
        {saving ? "Saving..." : "Lock in pick"}
      </button>
    </div>
  );
}
